import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MainLayout from '../components/layout/MainLayout';
import { useTheme } from '../context/ThemeContext';
import {
  Plus, Search, FileCheck, Clock, CheckCircle2, AlertCircle,
  Users, Calendar, MoreVertical, Eye, Download, Send, Sparkles,
  ArrowRight, FileText, Shield, Zap
} from 'lucide-react';

const contracts = [
  { id: 1, name: 'Service Agreement - ABC Corp', type: 'Service', parties: 2, status: 'active', value: '₹25L', expires: '15 Mar 2025', risk: 'low' },
  { id: 2, name: 'NDA - XYZ Ventures', type: 'NDA', parties: 3, status: 'review', value: '-', expires: '20 Jan 2025', risk: 'medium' },
  { id: 3, name: 'Employment Contract - Manager', type: 'Employment', parties: 2, status: 'pending', value: '₹18L/yr', expires: '31 Dec 2025', risk: 'low' },
  { id: 4, name: 'Lease Agreement - Office', type: 'Lease', parties: 2, status: 'expiring', value: '₹5L/mo', expires: '28 Jan 2025', risk: 'high' },
];

const statusColors = {
  active: 'bg-green-500/20 text-green-500',
  review: 'bg-blue-500/20 text-blue-500',
  pending: 'bg-yellow-500/20 text-yellow-500',
  expiring: 'bg-red-500/20 text-red-500',
};

const riskColors = {
  low: 'text-green-500',
  medium: 'text-yellow-500',
  high: 'text-red-500',
};

const Contracts = () => {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('all');

  return (
    <MainLayout title="Contracts (CLM)" subtitle="Contract Lifecycle Management">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        {[
          { label: 'Total Contracts', value: '48', icon: FileCheck, color: 'blue' },
          { label: 'Active', value: '32', icon: CheckCircle2, color: 'green' },
          { label: 'Pending Review', value: '8', icon: Clock, color: 'yellow' },
          { label: 'Expiring Soon', value: '4', icon: AlertCircle, color: 'red' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02, y: -5 }}
            className="glass-card p-6 relative overflow-hidden"
          >
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-${stat.color}-500 to-${stat.color}-400`} />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <h3 className="text-3xl font-bold mt-1">{stat.value}</h3>
              </div>
              <div className={`p-3 rounded-xl bg-${stat.color}-500/20`}>
                <stat.icon className={`w-6 h-6 text-${stat.color}-500`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* AI Contract Analysis Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 p-8 rounded-2xl relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(139,92,246,0.95) 0%, rgba(59,130,246,0.95) 100%)'
        }}
      >
        <motion.div
          className="absolute top-0 right-0 w-64 h-64 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="w-8 h-8 text-yellow-300" />
              <span className="px-3 py-1 text-xs bg-yellow-300/20 text-yellow-300 rounded-full">AI POWERED</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Smart Contract Analysis</h2>
            <p className="text-white/70 max-w-lg">
              Upload any contract and our AI will extract key clauses, identify risks, and suggest improvements instantly.
            </p>
          </div>
          <div className="flex gap-4">
            <motion.button
              className="px-6 py-4 rounded-xl bg-white/20 text-white font-semibold flex items-center gap-2 backdrop-blur-sm"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Shield className="w-5 h-5" />
              Risk Analysis
            </motion.button>
            <motion.button
              className="px-6 py-4 rounded-xl bg-white text-purple-600 font-semibold flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Zap className="w-5 h-5" />
              Upload Contract
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Action Bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-1 p-1 rounded-xl glass-card">
          {['all', 'active', 'review', 'expiring'].map((tab) => (
            <motion.button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${
                activeTab === tab ? 'bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] text-white' : ''
              }`}
              whileTap={{ scale: 0.95 }}
            >
              {tab}
            </motion.button>
          ))}
        </div>
        <motion.button
          className="flex items-center gap-2 px-5 py-3 rounded-xl btn-primary"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus className="w-5 h-5" />
          <span>New Contract</span>
        </motion.button>
      </div>

      {/* Contracts Table */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="glass-card overflow-hidden"
      >
        <table className="w-full">
          <thead>
            <tr className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8B] text-white">
              <th className="px-6 py-4 text-left">Contract Name</th>
              <th className="px-6 py-4 text-left">Type</th>
              <th className="px-6 py-4 text-left">Parties</th>
              <th className="px-6 py-4 text-left">Value</th>
              <th className="px-6 py-4 text-left">Expires</th>
              <th className="px-6 py-4 text-left">Risk</th>
              <th className="px-6 py-4 text-left">Status</th>
              <th className="px-6 py-4 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {contracts.map((contract, index) => (
              <motion.tr
                key={contract.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.05 }}
                className="border-b border-gray-500/10 hover:bg-blue-500/5 cursor-pointer"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-500/20">
                      <FileCheck className="w-5 h-5 text-purple-500" />
                    </div>
                    <span className="font-medium">{contract.name}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-500">{contract.type}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span>{contract.parties}</span>
                  </div>
                </td>
                <td className="px-6 py-4 font-semibold">{contract.value}</td>
                <td className="px-6 py-4 text-gray-500">{contract.expires}</td>
                <td className="px-6 py-4">
                  <span className={`font-medium capitalize ${riskColors[contract.risk]}`}>
                    {contract.risk}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 text-xs rounded-full capitalize ${statusColors[contract.status]}`}>
                    {contract.status}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    {[Eye, Download, Send].map((Icon, i) => (
                      <motion.button
                        key={i}
                        className="p-2 rounded-lg hover:bg-gray-500/10"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Icon className="w-4 h-4" />
                      </motion.button>
                    ))}
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </motion.div>
    </MainLayout>
  );
};

export default Contracts;
